import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PencilLine, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AppShell } from "@/components/AppShell";
import { EvidenceManager } from "@/components/EvidenceManager";
import { api, fmtKLDateTime } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const PAGE_SIZE = 50;

/* Dedicated review feed of every manually added clock-in/out (Batch 32).
   Entries can be deleted here by any reviewer (Batch 39 — archived server
   side, never destroyed); this page exists for oversight — plus attaching
   supporting evidence pictures (Batch 33), e.g. WhatsApp screenshots. */
export default function ManualPunchesPage() {
  const { t } = useLang();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pendingDelete, setPendingDelete] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: async (id) => (await api.delete(`/punches/${id}`)).data,
    onSuccess: () => {
      toast.success(t("punchDeleted"));
      setPendingDelete(null);
      queryClient.invalidateQueries();
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  const { data, isLoading } = useQuery({
    queryKey: ["manual-punches", page],
    queryFn: async () =>
      (await api.get("/punches/manual", { params: { page, pageSize: PAGE_SIZE } })).data,
  });
  const items = data?.items || [];
  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / PAGE_SIZE));

  return (
    <AppShell title={t("manualPunches")}>
      <div className="space-y-4" data-testid="manual-punches-page">
        <p className="text-sm text-muted-foreground">{t("manualPunchesHint")}</p>

        {isLoading && <Skeleton className="h-64 w-full rounded-xl" />}

        {!isLoading && items.length === 0 && (
          <div data-testid="manual-punches-empty"
            className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border p-10 text-center">
            <PencilLine className="h-7 w-7 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t("noManualPunches")}</p>
          </div>
        )}

        {!isLoading && items.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table data-testid="manual-punches-table" className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="p-3 font-semibold">{t("staff")}</th>
                  <th className="p-3 font-semibold">{t("punchType")}</th>
                  <th className="p-3 font-semibold">{t("punchTimeLabel")}</th>
                  <th className="p-3 font-semibold">{t("location")}</th>
                  <th className="p-3 font-semibold">{t("shiftLabel")}</th>
                  <th className="p-3 font-semibold">{t("addedBy")}</th>
                  <th className="p-3 font-semibold">{t("addedAt")}</th>
                  <th className="p-3 font-semibold">{t("note")}</th>
                  <th className="p-3 font-semibold">{t("evidence")}</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} data-testid="manual-punch-row"
                    className="border-b border-border last:border-0">
                    <td className="p-3">
                      <span className="font-medium">{p.userName}</span>
                      <span className="ml-1.5 text-xs text-muted-foreground">{p.employeeId}</span>
                    </td>
                    <td className="p-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        p.punchType === "clock_in"
                          ? "bg-emerald-50 text-emerald-900"
                          : "bg-rose-50 text-rose-900"}`}>
                        {p.punchType === "clock_in" ? t("clockIn") : t("clockOut")}
                      </span>
                    </td>
                    <td className="p-3 font-mono text-xs tabular-nums">{fmtKLDateTime(p.serverTimestamp)}</td>
                    <td className="p-3 text-xs">{p.siteName || "–"}</td>
                    <td className="p-3 text-xs">{p.shiftName || "–"}</td>
                    <td className="p-3 text-xs">{p.addedByName || "–"}</td>
                    <td className="p-3 font-mono text-xs tabular-nums">{p.reviewedAt ? fmtKLDateTime(p.reviewedAt) : "–"}</td>
                    <td className="p-3 max-w-[220px] truncate text-xs" title={p.overrideNote || ""}>{p.overrideNote || "–"}</td>
                    <td className="p-3">
                      <EvidenceManager punch={p} compact />
                    </td>
                    <td className="p-3 text-right">
                      <Button size="icon" variant="ghost" className="h-8 w-8"
                        title={t("deletePunch")} aria-label={t("deletePunch")}
                        data-testid="manual-punch-delete-button"
                        onClick={() => setPendingDelete(p)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1}
              data-testid="manual-punches-prev" onClick={() => setPage((v) => v - 1)}>
              {t("back")}
            </Button>
            <span className="text-xs text-muted-foreground">{page} / {totalPages}</span>
            <Button size="sm" variant="outline" disabled={page >= totalPages}
              data-testid="manual-punches-next" onClick={() => setPage((v) => v + 1)}>
              {t("next")}
            </Button>
          </div>
        )}
      </div>
      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent data-testid="manual-punch-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deletePunchTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete && (
                <>
                  <span className="font-medium text-foreground">{pendingDelete.userName}</span>
                  {" · "}{fmtKLDateTime(pendingDelete.serverTimestamp)}
                  <br />
                </>
              )}
              {t("deletePunchBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="manual-punch-delete-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="manual-punch-delete-confirm"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate(pendingDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
